import React, { useState } from "react";
import {
    Box, Typography, Grid, Card, CardContent, CardActions,
    Button, TextField, Drawer, IconButton, List, Divider,
    ListItemButton, ListItemIcon, ListItemText, Pagination,
    styled, useTheme
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import BuildIcon from "@mui/icons-material/Build";
import ComputerIcon from "@mui/icons-material/Computer";
import CategoryIcon from "@mui/icons-material/Category";
import TimerIcon from "@mui/icons-material/Timer";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import StarIcon from "@mui/icons-material/Star";

// ==========================================
// 1. STYLED COMPONENTS (Theme-Aware)
// ==========================================

const PageWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh",
    transition: "background-color 0.4s ease-in-out",
}));

const SidebarWrapper = styled(Box)(({ theme }) => ({
    width: 280,
    backgroundColor: "#09122C",
    color: "#FFFFFF",
    height: "100vh",
    position: "fixed",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(4, 2),
    zIndex: 1201,
}));

const MainContent = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    marginLeft: 280,
    padding: theme.spacing(6),
    [theme.breakpoints.down("md")]: {
        marginLeft: 0,
        padding: theme.spacing(3),
    },
}));

const StyledCard = styled(Card)(({ theme }) => {
    const isSky = theme.palette.background.default === "#BBE0EF";
    return {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        backgroundColor: "#FFFFFF",
        border: isSky ? "1px solid rgba(9, 18, 44, 0.1)" : "1px solid #f0f0f0",
        boxShadow: isSky
            ? "0 10px 25px rgba(9, 18, 44, 0.08)"
            : "0 4px 6px rgba(0,0,0,0.02)",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        position: "relative",
        "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 20px 40px rgba(9, 18, 44, 0.15)",
            borderColor: "#B8860B",
        },
    };
});

const Badge = styled(Box)({
    position: "absolute",
    top: 15,
    left: 15,
    backgroundColor: "#B8860B",
    color: "#000",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "0.65rem",
    fontWeight: 800,
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    zIndex: 2,
});

const EnrollButton = styled(Button)({
    width: "fit-content",
    backgroundColor: "#09122C",
    color: "#FFF",
    fontWeight: 700,
    textTransform: "none",
    padding: "10px 28px",
    borderRadius: "8px",
    fontSize: "0.9rem",
    "&:hover": {
        backgroundColor: "#B8860B",
        color: "#000",
    },
});

// ==========================================
// 2. HELPER SUB-COMPONENTS
// ==========================================

function HeroBanner() {
    return (
        <Box sx={{
            height: 240,
            borderRadius: "24px",
            mb: 8,
            display: "flex",
            alignItems: "center",
            px: { xs: 4, md: 8 },
            color: "white",
            background: `linear-gradient(135deg, #09122C 0%, #1A2A5A 100%)`,
            position: "relative",
            overflow: "hidden"
        }}>
            <Box sx={{ position: "absolute", right: -50, top: -50, width: 300, height: 300, borderRadius: "50%", border: "40px solid rgba(184, 134, 11, 0.1)" }} />
            <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography variant="h2" fontWeight={900} sx={{ fontSize: { xs: "2rem", md: "3.5rem" }, color: "#fdfdfd", mb: 1, letterSpacing: "-0.04em" }}>
                    Expert Training
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8, maxWidth: 500, fontWeight: 400 }}>
                    Access premium vocational courses and university-backed certifications designed for your success.
                </Typography>
            </Box>
        </Box>
    );
}

function SidebarContent({ selectedCategory, setSelectedCategory }) {
    const categories = ["All Courses", "Programming Courses", "Software Courses", "Vocational Courses"];
    const icons = {
        "All Courses": <CategoryIcon />,
        "Programming Courses": <CodeIcon />,
        "Software Courses": <ComputerIcon />,
        "Vocational Courses": <BuildIcon />,
    };

    return (
        <List sx={{ mt: 2 }}>
            {categories.map((cat) => (
                <ListItemButton
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    sx={{
                        borderRadius: "12px",
                        mb: 1,
                        py: 1.5,
                        bgcolor: selectedCategory === cat ? "rgba(184, 134, 11, 0.15)" : "transparent",
                        color: selectedCategory === cat ? "#B8860B" : "rgba(255,255,255,0.7)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.05)" }
                    }}
                >
                    <ListItemIcon sx={{ color: "inherit", minWidth: 45 }}>
                        {icons[cat]}
                    </ListItemIcon>
                    <ListItemText primary={cat} primaryTypographyProps={{ fontWeight: 700, fontSize: "0.95rem" }} />
                </ListItemButton>
            ))}
        </List>
    );
}

// ==========================================
// 3. MAIN PAGE COMPONENT
// ==========================================

export default function CoursePage() {
    const theme = useTheme();
    const isSkyMode = theme.palette.background.default === "#BBE0EF";

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All Courses");
    const [page, setPage] = useState(1);

    const categoryMap = {
        "All Courses": [...ProgrammingCourses, ...ProfessionalCourses, ...VocationalCourses],
        "Programming Courses": ProgrammingCourses,
        "Software Courses": ProfessionalCourses,
        "Vocational Courses": VocationalCourses,
    };

    const filteredCourses = categoryMap[selectedCategory].filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
    );

    const itemsPerPage = 8;
    const paginatedCourses = filteredCourses.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    return (
        <PageWrapper>
            {/* Desktop Sidebar */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
                <SidebarWrapper>
                    <Typography variant="h6" sx={{ color: "#B8860B", fontWeight: 900, px: 2, mb: 1 }}>
                        CRYSTAL CAREER
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", px: 2, mb: 4, letterSpacing: 1 }}>
                        LEARNING MANAGEMENT
                    </Typography>
                    <SidebarContent
                        selectedCategory={selectedCategory}
                        setSelectedCategory={(cat) => { setSelectedCategory(cat); setPage(1); }}
                    />
                </SidebarWrapper>
            </Box>

            {/* Mobile Drawer */}
            <Drawer open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
                <Box sx={{ width: 280, backgroundColor: "#09122C", height: "100%", color: "white", p: 2 }}>
                    <SidebarContent
                        selectedCategory={selectedCategory}
                        setSelectedCategory={(cat) => {
                            setSelectedCategory(cat);
                            setPage(1);
                            setSidebarOpen(false);
                        }}
                    />
                </Box>
            </Drawer>

            <MainContent>
                <IconButton
                    sx={{ display: { xs: "flex", md: "none" }, mb: 2, color: isSkyMode ? "#09122C" : "inherit" }}
                    onClick={() => setSidebarOpen(true)}
                >
                    <MenuIcon />
                </IconButton>

                <HeroBanner />

                {/* Filter & Search Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 6, flexWrap: "wrap", gap: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={900} color="#09122C" sx={{ letterSpacing: "-0.02em" }}>
                            {selectedCategory}
                        </Typography>
                        <Typography variant="body2" color={isSkyMode ? "#09122C" : "text.secondary"} sx={{ opacity: 0.8 }}>
                            Showing {filteredCourses.length} professional programs
                        </Typography>
                    </Box>

                    <TextField
                        variant="standard"
                        placeholder="Search programs..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        sx={{
                            width: { xs: "100%", sm: 300 },
                            "& .MuiInput-underline:before": { borderBottomColor: isSkyMode ? "#09122C" : "rgba(0,0,0,0.42)" },
                            "& .MuiInput-underline:after": { borderBottomColor: "#B8860B" },
                            "& input": { color: "#09122C", fontWeight: 600 }
                        }}
                    />
                </Box>

                {/* Course Grid */}
                <Grid container spacing={4}>
                    <AnimatePresence mode="popLayout">
                        {paginatedCourses.map((course, index) => (
                            <Grid item xs={12} sm={6} lg={3} key={course.name}>
                                <motion.div
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.4, delay: index * 0.05 }}
                                >
                                    <StyledCard>
                                        {index % 3 === 0 && (
                                            <Badge><StarIcon sx={{ fontSize: 12 }} /> Best Seller</Badge>
                                        )}
                                        <CardContent sx={{ p: 4, pt: 6 }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                                                <Typography variant="h6" fontWeight={800} color="#09122C" sx={{ lineHeight: 1.2 }}>
                                                    {course.name}
                                                </Typography>
                                                <IconButton size="small" sx={{ mt: -1 }}>
                                                    <FavoriteBorderIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 4, height: 60, overflow: "hidden" }}>
                                                {course.description}
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <TimerIcon sx={{ fontSize: 14, color: "#B8860B" }} />
                                                    <Typography variant="caption" fontWeight={700} color="#09122C">{course.duration}</Typography>
                                                </Box>
                                                <Typography variant="caption" fontWeight={900} color="#B8860B">{course.fees}</Typography>
                                            </Box>
                                        </CardContent>
                                        <CardActions sx={{ p: 4, pt: 0 }}>
                                            <EnrollButton fullWidth href="/plan-application-page" variant="contained">
                                                Enroll Now
                                            </EnrollButton>
                                        </CardActions>
                                    </StyledCard>
                                </motion.div>
                            </Grid>
                        ))}
                    </AnimatePresence>
                </Grid>

                {/* Pagination */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                    <Pagination
                        count={Math.ceil(filteredCourses.length / itemsPerPage)}
                        page={page}
                        onChange={(e, v) => setPage(v)}
                        size="large"
                        sx={{
                            "& .MuiPaginationItem-root": { fontWeight: 700, color: "#09122C" },
                            "& .Mui-selected": { bgcolor: "#09122C !important", color: "#fff" }
                        }}
                    />
                </Box>
            </MainContent>
        </PageWrapper>
    );
}

// ==========================================
// 4. DATA CONSTANTS
// ==========================================

const ProgrammingCourses = [
    { name: "JavaScript Essentials", description: "Master JavaScript fundamentals for dynamic web applications.", duration: "6 Weeks", fees: "£550" },
    { name: "Java Programming", description: "Learn core Java programming skills and object-oriented concepts.", duration: "8 Weeks", fees: "£500" },
    { name: "C++ Programming", description: "Advanced systems programming and performance optimization.", duration: "8 Weeks", fees: "£500" },
    { name: "Python Programming", description: "Data science and automation basics with the world's most popular language.", duration: "8 Weeks", fees: "£500" },
];

const ProfessionalCourses = [
    { name: "Web Development", description: "Full-stack web development course covering frontend and backend technologies.", duration: "12 Weeks", fees: "£1850" },
    { name: "Software Testing", description: "Modern QA techniques covering JUnit5, Mockito, and automated testing suites.", duration: "10 Weeks", fees: "£700" },
    { name: "MySQL Admin", description: "Learn database structure, query language, and high-performance manipulation.", duration: "8 Weeks", fees: "£500" },
    { name: "PostgreSQL Admin", description: "Master open-source relational databases for enterprise-level applications.", duration: "8 Weeks", fees: "£500" },
];

const VocationalCourses = [
    { name: "SIA Security", description: "Professional security industry training for licensing and front-line protection.", duration: "6 Weeks", fees: "£450" },
    { name: "Health & Social Care", description: "Compassionate training for careers in the UK care and support sector.", duration: "10 Weeks", fees: "£600" },
    { name: "Construction Skills", description: "Foundational hands-on training for construction and site management.", duration: "12 Weeks", fees: "£750" },
    { name: "Facility Management", description: "Strategic professional training in facility and asset lifecycle management.", duration: "8 Weeks", fees: "£500" },
];
